import type { EventArtist as GQLItem } from '../../../generated/api.graphql';
import * as Artist from './Artist';
import * as EventArtistRider from './EventArtistRider';
import { take } from '../../../util';

export type EventArtist = {
  id: number;
  artist?: Artist.Artist;
  riders?: EventArtistRider.EventArtistRider[];
  // Set times belong to the event artist, not to its riders: one pair per event, shared by the
  // hospitality and tech riders. That is the point of the v2 model — the legacy columns hung off
  // the event *group*, so one value covered every date in a run.
  onStage?: string | null;
  offStage?: string | null;
  headliner?: boolean;
  dressingRoomCurfew?: string | null;
  legacyEventArtistId?: number;
  legacySettlementSheetId?: number;
};

export type SetTimes = Pick<EventArtist, 'onStage' | 'offStage' | 'headliner' | 'dressingRoomCurfew'>;

// Bounded, not just shaped: `25:99` would otherwise pass straight through, and a caller parsing it
// as a wall clock gets an invalid date — the failure this function exists to stop.
const WALL_CLOCK = /^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d(?:\.\d+)?)?$/;

/**
 * Slices `"HH:MM:SS"` to `"HH:mm"` rather than parsing: the column is zone-less, and a `DateTime`
 * round-trip pushed these times four hours ahead. A shape it cannot express is refused, not passed on.
 */
export const toWallClockTime = (value?: string | null): string | null | undefined => {
  if (value === undefined || value === null) {
    return value;
  }

  const match = WALL_CLOCK.exec(value);

  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : null;
};

export const fromGql = (item: GQLItem): EventArtist => ({
  ...take<EventArtist>(item, 'id', 'legacyEventArtistId', 'legacySettlementSheetId', 'headliner'),
  artist: item.artist ? Artist.fromGql(item.artist) : undefined,
  riders: item.riders ? item.riders.map(EventArtistRider.fromGql) : undefined,
  onStage: toWallClockTime(item.onStage),
  offStage: toWallClockTime(item.offStage),
  dressingRoomCurfew: toWallClockTime(item.dressingRoomCurfew),
});

export const fromGqlCollection = (items: GQLItem[]): EventArtist[] => items.map(item => fromGql(item));
